import {
  CallOutcomeDto,
  FeedbackDescriptionDto,
  FilterOptionDto,
  MetadataDtoMap,
  NeedsActivityDto,
  NeedsCategoryDto,
  NeedsDomainDto,
  NeedsIssueDto,
  NeedsMetadataDto,
  RaceEthnicityDto,
  VisitOutcomeDto,
  ZCodeDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { needRequests } from 'redux/requests/need.requests';
import { memberSummaryRequests } from '../requests/member-summary-requests';
import { LoadingStatus } from '../types/shared-types';
import { createApiThunk } from '../utils/api-thunk';
import { SelectOption } from '@act/storybook';
import { userRequests } from 'redux/requests/users.request';
import {
  setToast as setToastAction,
  ToastConfig,
} from '../actions/toast-actions';
import { resetRedux } from '../actions/shared-actions';
import { verifyUser } from './actingUserSlice';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { selectMember } from './selectedMemberSlice';

type NeedsMetadataSuccessPayload = {
  data:
    | NeedsActivityDto
    | NeedsCategoryDto
    | NeedsDomainDto
    | NeedsIssueDto
    | ZCodeDto;
  type: keyof MetadataDtoMap;
};

// THUNKS/ACTIONS ==============================================================
export const getFeedbackDescriptions = createApiThunk(
  memberSummaryRequests.getFeedbackDescriptions
);
export const getNeedsMetadata = createApiThunk(needRequests.getNeedsMetadata);

export const getRaceEthnicityOptions = createApiThunk(
  userRequests.getRaceEthnicityOptions
);

export const createNeedsMetadata = createApiThunk(
  needRequests.createNeedsMetadata
);
export const updateNeedsMetadata = createApiThunk(
  needRequests.updateNeedsMetadata
);

export const createNeedsMetadataSuccess =
  createAction<NeedsMetadataSuccessPayload>(
    'needsMetadata/createNeedsMetadataSuccess'
  );
export const updateNeedsMetadataSuccess =
  createAction<NeedsMetadataSuccessPayload>(
    'needsMetadata/updateNeedsMetadataSuccess'
  );
export const setOutcomes = createAction<{
  voiceCallOutcomes: CallOutcomeDto[];
  visitOutcomes: VisitOutcomeDto[];
}>('metaData/setOutcomes');
// INITIAL STATE ===============================================================
export interface MetaSliceState {
  memberFeedbackDescriptions: {
    collection: FeedbackDescriptionDto[];
    status: LoadingStatus;
  };
  needsMetadata: {
    data: NeedsMetadataDto;
    status: LoadingStatus;
  };
  raceEthnicityOptions: { data: RaceEthnicityDto[]; status: LoadingStatus };
  toastConfig: ToastConfig;
  voiceCallOutcomes: CallOutcomeDto[];
  visitOutcomes: VisitOutcomeDto[];
}

const initialState: MetaSliceState = {
  memberFeedbackDescriptions: {
    status: 'initial',
    collection: [],
  },
  needsMetadata: {
    status: 'initial',
    data: null,
  },
  toastConfig: {
    message: null,
    severity: null,
    details: null,
  },
  voiceCallOutcomes: [],
  visitOutcomes: [],
  raceEthnicityOptions: { status: 'initial', data: null },
};

// SLICE =======================================================================
export const metaSlice = createSlice({
  name: 'metaData',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(setToastAction, (state, action) => {
        return {
          ...state,
          toastConfig: {
            message: action.payload.message,
            severity: action.payload.severity,
            ...(action.payload.details && { details: action.payload.details }),
          },
        };
      })
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      // Get Feedback Descriptions =====
      .addCase(getFeedbackDescriptions.pending, state => {
        state.memberFeedbackDescriptions.status = 'loading';
      })
      .addCase(getFeedbackDescriptions.fulfilled, (state, action) => {
        state.memberFeedbackDescriptions.status = 'success';
        state.memberFeedbackDescriptions.collection = action.payload;
      })
      .addCase(getFeedbackDescriptions.rejected, state => {
        state.memberFeedbackDescriptions.status = 'failed';
        state.memberFeedbackDescriptions.collection = [];
      })

      .addCase(getNeedsMetadata.pending, state => {
        state.needsMetadata.status = 'loading';
      })
      .addCase(
        getNeedsMetadata.fulfilled,
        (state, action: PayloadAction<NeedsMetadataDto>) => {
          state.needsMetadata.status = 'success';
          state.needsMetadata.data = action.payload;
        }
      )
      .addCase(getNeedsMetadata.rejected, state => {
        state.needsMetadata.status = 'failed';
      })

      .addCase(getRaceEthnicityOptions.pending, state => {
        state.raceEthnicityOptions.status = 'loading';
      })
      .addCase(getRaceEthnicityOptions.fulfilled, (state, action) => {
        state.raceEthnicityOptions.status = 'success';
        state.raceEthnicityOptions.data = action.payload;
      })
      .addCase(getRaceEthnicityOptions.rejected, state => {
        state.raceEthnicityOptions.status = 'failed';
      })
      .addCase(
        updateNeedsMetadataSuccess,
        (state, action: PayloadAction<NeedsMetadataSuccessPayload>) => {
          const { type, data } = action.payload;
          const list = state.needsMetadata.data[type];
          state.needsMetadata.status = 'success';
          state.needsMetadata.data[type] = list.map(item =>
            item.id === data.id ? data : item
          );
        }
      )

      .addCase(
        createNeedsMetadataSuccess,
        (state, action: PayloadAction<NeedsMetadataSuccessPayload>) => {
          const { type, data } = action.payload;

          state.needsMetadata.status = 'success';
          state.needsMetadata.data[type].push(data as any);
        }
      )

      // NOTE: addMatcher must come AFTER all addCase calls
      .addMatcher(
        verifyUser.fulfilled.match,
        (
          state,
          action: PayloadAction<{
            voiceCallOutcomes: CallOutcomeDto[];
            visitOutcomes: VisitOutcomeDto[];
          }>
        ) => {
          state.voiceCallOutcomes = action.payload.voiceCallOutcomes;
          state.visitOutcomes = action.payload.visitOutcomes;
        }
      );
  },
});

// REDUCER =====================================================================
export const metaReducer = metaSlice.reducer;

// ACTIONS =====================================================================
export const metaActions = metaSlice.actions;
export const setToast = setToastAction;

// SELECTORS ===================================================================
interface MetaSliceSelectorProps {
  includeEngagedStatus?: boolean;
  targetOutcomeType?: 'resourcing' | 'interaction';
  callCreatedAt?: number;
  visitCreatedAt?: number;
  [key: string]: unknown;
}

const selectMetaSliceState = state =>
  (state as { meta: typeof initialState })?.meta;

export const selectFeedbackDescriptions = state =>
  selectMetaSliceState(state).memberFeedbackDescriptions;

export const selectNeedsMetadata = state =>
  selectMetaSliceState(state).needsMetadata;

export const selectNeedsDomainByIdFromMetadata = (
  metadataState: NeedsMetadataDto,
  id: number
) => metadataState?.domains.find(d => d.id === id);
export const selectNeedsCategoryByIdFromMetadata = (
  metadataState: NeedsMetadataDto,
  id: number
) => metadataState?.categories.find(c => c.id === id);
export const selectNeedsIssueByIdFromMetadata = (
  metadataState: NeedsMetadataDto,
  id: number
) => metadataState?.issues.find(i => i.id === id);
export const selectNeedsZcodeByIdFromMetadata = (
  metadataState: NeedsMetadataDto,
  id: number
) => metadataState?.zcodes.find(z => z.id === id);

const selectNeedIssueOptions: (state) => FilterOptionDto[] = createSelector(
  selectNeedsMetadata,
  meta => {
    if (!meta?.data?.issues) return [];

    // we cant sort an immutable array, copy it first.
    const arrayForSort = [...meta.data.issues];
    const sortedIssues = arrayForSort.sort(
      (a, b) => a.categoryId - b.categoryId
    );

    return sortedIssues.map(i => {
      const tagCloud = i.tagCloud?.split(',') || [];
      tagCloud.unshift(
        meta.data.categories.find(c => c.id === i.categoryId).label
      );
      return {
        label: i.label,
        value: i.id,
        tagCloud,
      };
    });
  }
);

export const selectResourceResearchActivity: (state) => NeedsActivityDto =
  createSelector(selectNeedsMetadata, meta =>
    meta.data.activities.find(a => a.key === 'activity_resource-research')
  );

export const selectToastConfig = state =>
  selectMetaSliceState(state).toastConfig;

const selectCallOutcomes = state =>
  selectMetaSliceState(state).voiceCallOutcomes;
const selectVisitOutcomes = state => selectMetaSliceState(state).visitOutcomes;

const filterSelectOutcomes = (
  outcomes,
  targetOutcomeType,
  includeEngagedStatus,
  createdAt
) => {
  const filteredOutcomes = outcomes
    .filter(o => {
      return o.outcomeTypes.includes(targetOutcomeType);
    })
    .filter(outcome => {
      if (createdAt) {
        return !outcome.deprecatedAt || outcome.deprecatedAt >= createdAt;
      } else {
        return outcome.deprecatedAt === null;
      }
    })
    .filter(outcome => {
      if (!includeEngagedStatus) {
        return outcome.key !== 'engaged';
      }
      return true;
    });

  return filteredOutcomes.map(o => {
    return {
      label: o.text,
      value: o.id,
    };
  });
};

const selectCallOutcomeOptions: (
  state,
  props: MetaSliceSelectorProps
) => SelectOption<number>[] = createSelector(
  [
    selectCallOutcomes,
    (state, props: MetaSliceSelectorProps) => props.targetOutcomeType,
    (state, props: MetaSliceSelectorProps) => props.includeEngagedStatus,
    (state, props: MetaSliceSelectorProps) => props.callCreatedAt,
  ],
  (outcomes, targetOutcomeType, includeEngagedStatus, createdAt) => {
    return filterSelectOutcomes(
      outcomes,
      targetOutcomeType,
      includeEngagedStatus,
      createdAt
    );
  }
);

const selectVisitOutcomeOptions: (
  state,
  props: MetaSliceSelectorProps
) => SelectOption<number>[] = createSelector(
  [
    selectVisitOutcomes,
    (state, props: MetaSliceSelectorProps) => props.targetOutcomeType,
    (state, props: MetaSliceSelectorProps) => props.visitCreatedAt,
  ],
  (outcomes, targetOutcomeType, createdAt) => {
    return filterSelectOutcomes(outcomes, targetOutcomeType, true, createdAt);
  }
);

const selectCallOutcomeById = (id: number) =>
  createSelector(selectCallOutcomes, callOutcomes => {
    return callOutcomes.find(o => o.id === id);
  });
const selectVisitOutcomeById = (id: number) =>
  createSelector(selectVisitOutcomes, visitOutcomes => {
    return visitOutcomes.find(o => o.id === id);
  });

const selectRaceEthnicityOptions = state =>
  selectMetaSliceState(state).raceEthnicityOptions.data;

// PLAIN SELECTORS (for use with useAppSelector in components) =================
export const metaSelectors = {
  selectFeedbackDescriptions,
  selectNeedsMetadata,
  selectNeedIssueOptions,
  selectResourceResearchActivity,
  selectToastConfig,
  selectRaceEthnicityOptions,
  selectCallOutcomes,
  selectCallOutcomeOptions,
  selectCallOutcomeById,
  selectVisitOutcomes,
  selectVisitOutcomeOptions,
  selectVisitOutcomeById,
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: verifyUser.fulfilled,
    effect: async ({ dispatch }) => {
      dispatch(getNeedsMetadata({}));
      dispatch(getRaceEthnicityOptions({}));
    },
  });
  addEffect({
    action: selectMember,
    effect: async ({ dispatch }) => {
      dispatch(getRaceEthnicityOptions({}));
    },
  });
});
